import {
    useCentralErrorSetter,
    useGetErrorInfo,
} from '@experiences/error';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    type IRelayEndpointResponseDto,
    type IRelayGroupMetadata,
    type IUiDialogHookCustomContent,
    PermissionAction,
    RelayAgentHealthStatus,
    RelayClientSupportStatus,
    RelayEndpointScheme,
    RelayHealthStatus,
} from '@experiences/interfaces';
import {
    SpacingToken,
    UiCopyButton,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import {
    useNavigateWithParams,
    useRouteResolver,
    useShowDialog,
} from '@experiences/util';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardActions,
    CardContent,
    InputAdornment,
    TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApAlertBar,
    ApButton,
    ApDataGridColumn,
    ApDataGridFooter,
    ApDataGridHeader,
    ApDataGridRowActions,
    ApDataGridRowButton,
    ApDataGridWrapper,
    ApIconButton,
    ApLink,
    ApTooltip,
    PortalCustomIcon,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useMemo,
} from 'react';
import {
    FormattedMessage,
    useIntl,
} from 'react-intl';
import { useSelector } from 'react-redux';
import {
    generatePath,
    useParams,
} from 'react-router';

import { notificationType } from '../../common/constants/Constant';
import { routePaths } from '../../common/constants/routePaths';
import { useUiSnackBar } from '../../common/hooks/useUiSnackBar';
import { useBreadcrumbs } from '../../common/providers/BreadcrumbProvider';
import { useTenantIdContext } from '../../common/providers/TenantIdProvider';
import { deleteRelayAgent } from '../../services/relay/RelayService';
import {
    accountGlobalId,
    accountLogicalName,
} from '../../store/selectors';
import UiPageContainer from '../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../organizationsettings/AdminBreadCrumbs';
import { isTcpEndpointUrl } from './relayEndpointUtil';
import useRelayGroupDetailsViewModel from './RelayGroupDetailsViewModel';
import { useHasRelayPermission } from './useRelayPermission';

const useStyles = makeStyles(theme => ({
    groupDetailsContainer: { paddingBottom: theme.spacing(3) },
    cardLayout: {
        display: 'flex',
        width: 360,
        flexDirection: 'column',
    },
    computerIcon: {
        color: theme.palette.primary.main,
        fontSize: 32,
    },
    updateAvailableIcon: {
        fontSize: 18,
        flexShrink: 0,
    },
    agentsAccordion: {
        '&.MuiAccordion-root': {
            boxShadow: 'none',
            margin: theme.spacing(0, 0, 4, 0),
            backgroundImage: 'none',
            backgroundColor: 'inherit',
            '&::before': { display: 'none' },
        },
        '& .MuiAccordionSummary-root': {
            padding: 0,
            minHeight: 'unset',
            '&:hover': { backgroundColor: 'inherit' },
        },
        '& .MuiAccordionSummary-content': { margin: theme.spacing(0, 0, 1.5, 0) },
        '& .MuiAccordionDetails-root': { padding: 0 },
    },
    agentsInfoText: { marginTop: theme.spacing(2) },
    agentHostname: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 180,
    },
    groupText: { marginBottom: theme.spacing(1) },
    endPointListContainer: {
        marginTop: theme.spacing(1),
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto',
    },
    cardActions: {
        justifyContent: 'flex-end',
        paddingTop: 0,
    },
}));

const RelayGroupDetailsComponent: React.FC<{
    relayGroupDetails?: IRelayGroupMetadata;
    tenantId?: string;
    relayGroupId?: string;
}> = ({
    relayGroupDetails, tenantId, relayGroupId,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const navigate = useNavigateWithParams();
    const getRoute = useRouteResolver();

    const hasEditPermission = useHasRelayPermission([ PermissionAction.Relay.RelayGroup.Edit ], tenantId);

    const handleEdit = useCallback(() => {
        if (tenantId && relayGroupId) {
            navigate(getRoute(generatePath(routePaths.adminTenantRelayEdit, {
                tenantId,
                relayGroupId,
            })));
        }
    }, [ navigate, getRoute, tenantId, relayGroupId ]);

    const onClickInstructions = useCallback(() => {
        if (tenantId && relayGroupId) {
            navigate(getRoute(generatePath(routePaths.adminTenantRelaySetupInstructions, {
                tenantId,
                relayGroupId,
                type: 'view',
            })));
        }
    }, [ navigate, getRoute, tenantId, relayGroupId ]);

    if (!relayGroupDetails) {
        return null;
    }

    return (
        <div
            className={classes.groupDetailsContainer}>
            <UiStack
                direction='row'
                justify='between'>
                <UiStack direction='column'>
                    <UiText
                        variant={FontVariantToken.fontSizeH3Bold}
                        className={classes.groupText}>
                        {relayGroupDetails.name}
                    </UiText>
                    <UiText variant={FontVariantToken.fontSizeM}>
                        {translate({ id: 'CLIENT_RELAY_GROUP_ID' }, { relayGroupId: relayGroupDetails.id })}
                    </UiText>
                    {relayGroupDetails.description && (
                        <UiText
                            variant={FontVariantToken.fontSizeM}
                            className={classes.groupText}>
                            {relayGroupDetails.description}
                        </UiText>
                    )}
                </UiStack>
                <UiStack
                    direction='row'
                    gap={SpacingToken.XS}>
                    <ApButton
                        label={translate({ id: 'CLIENT_EDIT' })}
                        variant="secondary"
                        onClick={handleEdit}
                        disabled={!hasEditPermission}
                        data-cy="edit-relay-group-button"
                    />
                    <ApButton
                        label={translate({ id: 'CLIENT_VIEW_SETUP_INSTRUCTIONS' })}
                        variant="secondary"
                        onClick={onClickInstructions}
                        data-cy="view-setup-instructions-button"
                    />
                </UiStack>
            </UiStack>
        </div>
    );
};

interface IRelayEndpointUrlDialogProps {
    endpoint: IRelayEndpointResponseDto;
    mappedUrl: string;
}

const CopyableUrl: React.FC<{ url: string; dataCy: string }> = ({
    url, dataCy,
}) => (
    <TextField
        data-cy={dataCy}
        value={url}
        fullWidth
        InputProps={{
            readOnly: true,
            endAdornment: (
                <InputAdornment position="end">
                    <UiCopyButton textToCopy={url} />
                </InputAdornment>
            ),
        }}
    />
);

const RelayEndpointUrlDialogBody: React.FC<IUiDialogHookCustomContent & IRelayEndpointUrlDialogProps> = ({
    closeDialog, endpoint, mappedUrl,
}) => {
    const { formatMessage: translate } = useIntl();
    const isTcp = isTcpEndpointUrl(endpoint.url);

    return (
        <UiStack
            direction="column"
            gap={SpacingToken.M}
            m={SpacingToken.Micro}>
            <UiStack
                direction="column"
                gap={SpacingToken.XS}>
                <UiText variant={FontVariantToken.fontSizeMBold}>
                    {translate({ id: 'CLIENT_RELAY_ENDPOINT_HOST_URL' })}
                </UiText>
                <UiText
                    variant={FontVariantToken.fontSizeS}
                    color="textSecondary">
                    {translate({ id: 'CLIENT_RELAY_ENDPOINT_HOST_URL_DESCRIPTION' })}
                </UiText>
                <CopyableUrl
                    url={endpoint.url}
                    dataCy="relay-endpoint-host-url" />
            </UiStack>
            {!isTcp && (
                <UiStack
                    direction="column"
                    gap={SpacingToken.XS}>
                    <UiText variant={FontVariantToken.fontSizeMBold}>
                        {translate({ id: 'CLIENT_RELAY_ENDPOINT_MAPPED_URL' })}
                    </UiText>
                    <UiText
                        variant={FontVariantToken.fontSizeS}
                        color="textSecondary">
                        {translate({ id: 'CLIENT_RELAY_ENDPOINT_MAPPED_URL_DESCRIPTION' })}
                    </UiText>
                    <CopyableUrl
                        url={mappedUrl}
                        dataCy="relay-endpoint-mapped-url" />
                </UiStack>
            )}
            <UiStack
                direction="row"
                justify="end">
                <ApButton
                    variant="primary"
                    data-cy="relay-endpoint-url-dialog-close"
                    onClick={() => closeDialog(true)}
                    label={translate({ id: 'CLIENT_CLOSE' })}
                />
            </UiStack>
        </UiStack>
    );
};

const RelayEndpointsListComponent: React.FC = () => {
    const { formatMessage: translate } = useIntl();
    const {
        relayEndpoints, isEndpointLoading, onEndpointsGridChange, refreshEndpoints, endpointPagination, healthStatusMap,
    } = useRelayGroupDetailsViewModel();
    const accountName = useSelector(accountLogicalName);
    const { tenantName } = useTenantIdContext();
    const createDialog = useShowDialog();

    const classes = useStyles();

    const buildMappedUrl = useCallback(
        (row: IRelayEndpointResponseDto) =>
            `${window.location.origin}/${accountName}/${tenantName}/relay_/connect/${row.id}`,
        [ accountName, tenantName ],
    );

    const openUrlMappingDialog = useCallback(
        (row: IRelayEndpointResponseDto) => createDialog({
            title: translate({ id: 'CLIENT_RELAY_ENDPOINT_URL_MAPPING' }),
            icon: 'info',
            hideActions: true,
            width: '600px',
            customDialogContent: RelayEndpointUrlDialogBody,
            customDialogContentProps: {
                endpoint: row,
                mappedUrl: buildMappedUrl(row),
            },
        }),
        [ createDialog, translate, buildMappedUrl ],
    );

    const endpointsData: IRelayEndpointResponseDto[] = useMemo(
        () => (relayEndpoints?.results ?? []).map(ep => ({
            ...ep,
            connectivityStatus: healthStatusMap?.[ep.id] ?? ep.connectivityStatus,
        })),
        [ relayEndpoints, healthStatusMap ],
    );

    const renderHealthStatus = useCallback((row: IRelayEndpointResponseDto) => {
        let status: 'success' | 'warning' | 'error' | 'default';
        let label: string;
        let tooltipText: string;

        if (row.connectivityStatus === RelayHealthStatus.Live) {
            status = 'success';
            label = translate({ id: 'CLIENT_RELAY_HEALTH_STATUS_LIVE' });
            tooltipText = translate({ id: 'CLIENT_RELAY_HEALTH_STATUS_LIVE_TOOLTIP' });
        } else if (row.connectivityStatus === RelayHealthStatus.Warning) {
            status = 'warning';
            label = translate({ id: 'CLIENT_RELAY_HEALTH_STATUS_WARNING' });
            tooltipText = translate({ id: 'CLIENT_RELAY_HEALTH_STATUS_WARNING_TOOLTIP' });
        } else if (row.connectivityStatus === RelayHealthStatus.Offline) {
            status = 'error';
            label = translate({ id: 'CLIENT_RELAY_HEALTH_STATUS_OFFLINE' });
            tooltipText = row.scheme === RelayEndpointScheme.Tcp
                ? translate({ id: 'CLIENT_RELAY_HEALTH_STATUS_TCP_OFFLINE_TOOLTIP' })
                : translate({ id: 'CLIENT_RELAY_HEALTH_STATUS_OFFLINE_TOOLTIP' });
        } else {
            status = 'default';
            label = translate({ id: 'CLIENT_RELAY_STATUS_UNKNOWN' });
            tooltipText = translate({ id: 'CLIENT_RELAY_STATUS_UNKNOWN_TOOLTIP' });
        }

        return (
            <ApTooltip
                content={tooltipText}
                placement='top-start'>
                <ap-badge
                    data-cy="endpoint-health-chip"
                    label={label}
                    size="large"
                    status={status} />
            </ApTooltip>
        );
    }, [ translate ]);

    return (
        <>
            <UiText
                variant={FontVariantToken.fontSizeLBold}>
                {translate({ id: 'CLIENT_ENDPOINT_URLS' })}
            </UiText>
            <div
                data-cy="relay-endpoints-list"
                className={classes.endPointListContainer}>
                <ApDataGridWrapper<IRelayEndpointResponseDto>
                    data={endpointsData}
                    onChange={onEndpointsGridChange}
                    loading={isEndpointLoading}
                    refreshable
                    selectable={false}
                    refresh={refreshEndpoints}
                    dataCy='relay-endpoints-grid'
                >
                    <ApDataGridHeader<IRelayEndpointResponseDto> search />
                    <ApDataGridColumn<IRelayEndpointResponseDto>
                        property="name"
                        title={translate({ id: 'CLIENT_NAME' })} />
                    <ApDataGridColumn<IRelayEndpointResponseDto>
                        property="connectivityStatus"
                        title={translate({ id: 'CLIENT_STATUS' })}
                        render={renderHealthStatus} />
                    <ApDataGridRowActions>
                        <ApDataGridRowButton<IRelayEndpointResponseDto>
                            id='view-urls-button'
                            label={translate({ id: 'CLIENT_VIEW_URLS' })}
                            render={row => (
                                <ApTooltip content={translate({ id: 'CLIENT_VIEW_URLS' })}>
                                    <ApIconButton
                                        label={translate({ id: 'CLIENT_VIEW_URLS' })}
                                        color="secondary"
                                        onClick={() => openUrlMappingDialog(row)}
                                        data-cy="view-urls-button">
                                        <LinkOutlinedIcon />
                                    </ApIconButton>
                                </ApTooltip>
                            )}
                        />
                    </ApDataGridRowActions>
                    <ApDataGridFooter
                        pageSizes={[ 5, 10 ]}
                        length={relayEndpoints?.totalCount ?? 0}
                        pageSize={endpointPagination.top}
                        pageIndex={Math.floor(endpointPagination.skip / endpointPagination.top)}
                    />
                </ApDataGridWrapper>
            </div>
        </>);
};

const RelayAgentsComponent: React.FC<{ tenantId?: string }> = ({ tenantId }) => {
    const { formatMessage: translate } = useIntl();
    const theme = useTheme();
    const accountId = useSelector(accountGlobalId);
    const { createNotificationWithOptions } = useUiSnackBar();
    const createDialog = useShowDialog();
    const setErrorMessage = useCentralErrorSetter();
    const { getErrorMessage } = useGetErrorInfo();
    const {
        relayAgents, refreshAgents, agentHealthStatusMap,
    } = useRelayGroupDetailsViewModel();
    const classes = useStyles();
    const relayDownloadLink = useFeatureFlagValue(Features.RelayDownloadSoftwareLink.name, Features.RelayDownloadSoftwareLink.defaultValue);
    const hasClientDeletePermission = useHasRelayPermission([ PermissionAction.Relay.RelayClient.Delete ], tenantId);

    const updateAvailableCount = useMemo(
        () => (relayAgents?.results ?? []).filter(
            agent => agent.supportStatus === RelayClientSupportStatus.UpdateAvailable
                || agent.supportStatus === RelayClientSupportStatus.OutOfSupport,
        ).length,
        [ relayAgents ],
    );

    const handleDeleteAgent = useCallback(async (agentId: string) => {
        const deleteConfirmed = await createDialog({
            title: translate({ id: 'CLIENT_DELETE_RELAY_AGENT_CONFIRMATION_TITLE' }),
            body: translate({ id: 'CLIENT_DELETE_RELAY_AGENT_CONFIRMATION_MESSAGE' }),
            icon: 'warning',
            showCancel: true,
            primaryButtonText: translate({ id: 'CLIENT_DELETE' }),
        });
        if (deleteConfirmed && tenantId) {
            try {
                await deleteRelayAgent(accountId, tenantId, agentId);
                createNotificationWithOptions({
                    message: translate({ id: 'CLIENT_RELAY_AGENT_DELETE_SUCCESS' }),
                    type: notificationType.SUCCESS,
                    title: translate({ id: 'CLIENT_DELETED' }),
                });
                refreshAgents();
            } catch (error) {
                setErrorMessage(await getErrorMessage(error));
            }
        }
    }, [ createDialog, translate, accountId, tenantId, setErrorMessage, getErrorMessage, createNotificationWithOptions, refreshAgents ]);

    return (
        <Accordion
            defaultExpanded
            className={classes.agentsAccordion}
            data-cy="relay-agents-section">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <UiText variant={FontVariantToken.fontSizeLBold}>
                    {translate({ id: 'CLIENT_RELAY_AGENTS' })}
                </UiText>
            </AccordionSummary>
            <AccordionDetails>
                {relayAgents?.results.length === 0 && (
                    <UiText variant={FontVariantToken.fontSizeM}>
                        {translate({ id: 'CLIENT_NO_RELAY_AGENTS' })}
                    </UiText>
                )}
                {/* For now we show a single banner covering both "update available" and "out of support"
                    agents. Splitting these into separate, severity-specific banners will be addressed later. */}
                {updateAvailableCount > 0 && (
                    <UiStack
                        direction="column"
                        gap={SpacingToken.XS}
                        mb={SpacingToken.Base}>
                        {updateAvailableCount > 0 && (
                            <ApAlertBar
                                cancelable={false}
                                status="warning"
                                data-cy="relay-agents-update-available-banner">
                                <UiText>
                                    <FormattedMessage
                                        id={updateAvailableCount === 1
                                            ? 'CLIENT_RELAY_AGENTS_UPDATE_AVAILABLE_BANNER_SINGULAR'
                                            : 'CLIENT_RELAY_AGENTS_UPDATE_AVAILABLE_BANNER_PLURAL'}
                                        values={{
                                            count: updateAvailableCount,
                                            a: (chunks: React.ReactNode[]) => (
                                                <ApLink
                                                    href={relayDownloadLink as string}
                                                    target="_blank"
                                                    rel="noopener noreferrer">
                                                    {chunks}
                                                </ApLink>
                                            ),
                                        }}
                                    />
                                </UiText>
                            </ApAlertBar>
                        )}
                    </UiStack>
                )}
                <UiStack
                    wrap="wrap"
                    gap={SpacingToken.Base}
                    data-cy="relay-agents-cards">
                    {(relayAgents?.results ?? []).map(agent => {
                        const healthStatus = agentHealthStatusMap[agent.id] ?? agent.healthStatus;
                        const isActive = healthStatus === RelayAgentHealthStatus.Active;
                        const deleteLabel = isActive
                            ? translate({ id: 'CLIENT_RELAY_AGENT_DELETE_DISABLED' })
                            : translate({ id: 'CLIENT_DELETE' });
                        return (
                            <Card
                                key={agent.id}
                                variant="outlined"
                                className={classes.cardLayout}
                                data-cy={`agent-card-${agent.id}`}>
                                <CardContent>
                                    <UiStack
                                        direction="row"
                                        justify="between"
                                        align="start">
                                        <UiStack
                                            direction="row"
                                            align="center"
                                            gap={SpacingToken.XS}
                                            style={{
                                                minWidth: 0,
                                                overflow: 'hidden',
                                            }}>
                                            <ComputerOutlinedIcon
                                                className={classes.computerIcon}
                                                aria-label={translate({ id: 'CLIENT_RELAY_AGENT' })} />
                                            <UiText
                                                variant={FontVariantToken.fontSizeMBold}
                                                className={classes.agentHostname}>
                                                {agent.hostname}
                                            </UiText>
                                        </UiStack>
                                        <Box sx={{ mt: theme.spacing(0.5) }}>
                                            <ApTooltip
                                                content={isActive
                                                    ? translate({ id: 'CLIENT_RELAY_AGENT_ACTIVE_TOOLTIP' })
                                                    : translate({ id: 'CLIENT_RELAY_AGENT_INACTIVE_TOOLTIP' })}
                                                placement='top-start'>
                                                <ap-badge
                                                    status={isActive ? 'success' : 'error'}
                                                    label={isActive
                                                        ? translate({ id: 'CLIENT_ACTIVE' })
                                                        : translate({ id: 'CLIENT_INACTIVE' })}
                                                    size="large"
                                                    data-cy="agent-health-chip"
                                                    style={{ flexShrink: 0 }} />
                                            </ApTooltip>
                                        </Box>
                                    </UiStack>
                                    <UiStack
                                        gap={SpacingToken.XXL}
                                        className={classes.agentsInfoText}>
                                        <UiStack direction="column">
                                            <UiText
                                                variant={FontVariantToken.fontSizeS}
                                                color={theme.palette.text.secondary}>
                                                {translate({ id: 'CLIENT_VERSION' })}
                                            </UiText>
                                            <UiStack
                                                direction="row"
                                                align="center"
                                                gap={SpacingToken.XS}>
                                                <UiText variant={FontVariantToken.fontSizeM}>
                                                    {agent.version}
                                                </UiText>
                                                {agent.supportStatus === RelayClientSupportStatus.UpdateAvailable && (
                                                    <ApTooltip
                                                        content={translate({ id: 'CLIENT_RELAY_AGENT_UPDATE_AVAILABLE' })}
                                                        placement='top-start'>
                                                        <PortalCustomIcon
                                                            name="warning"
                                                            size="18px"
                                                            className={classes.updateAvailableIcon}
                                                            data-cy="agent-update-available-icon"
                                                            aria-label={translate({ id: 'CLIENT_RELAY_AGENT_UPDATE_AVAILABLE' })} />
                                                    </ApTooltip>
                                                )}
                                            </UiStack>
                                        </UiStack>
                                        <UiStack direction="column">
                                            <UiText
                                                variant={FontVariantToken.fontSizeS}
                                                color={theme.palette.text.secondary}>
                                                {translate({ id: 'CLIENT_PLATFORM' })}
                                            </UiText>
                                            <UiText variant={FontVariantToken.fontSizeM}>
                                                {agent.platform}
                                            </UiText>
                                        </UiStack>
                                        <UiStack direction="column">
                                            <UiText
                                                variant={FontVariantToken.fontSizeS}
                                                color={theme.palette.text.secondary}>
                                                {translate({ id: 'CLIENT_EXTERNAL_APP_SECRET_ID' })}
                                            </UiText>
                                            <UiText variant={FontVariantToken.fontSizeM}>
                                                {agent.secretId ?? '-'}
                                            </UiText>
                                        </UiStack>
                                    </UiStack>
                                </CardContent>
                                <CardActions className={classes.cardActions}>
                                    <ApTooltip content={deleteLabel}>
                                        <ApIconButton
                                            label={deleteLabel}
                                            data-cy="delete-button"
                                            color="secondary"
                                            disabled={isActive || !hasClientDeletePermission}
                                            onClick={() => handleDeleteAgent(agent.id)}>
                                            <DeleteOutlinedIcon aria-label={deleteLabel} />
                                        </ApIconButton>
                                    </ApTooltip>
                                </CardActions>
                            </Card>
                        );
                    })}
                </UiStack>
            </AccordionDetails>
        </Accordion>
    );
};

const RelayGroupDetailsPageComponent: React.FC = () => {
    const {
        tenantId, relayGroupId,
    } = useParams<{ tenantId: string; relayGroupId: string }>();
    const { breadcrumbs } = useBreadcrumbs();

    const {
        relayGroupDetails, loading,
    } = useRelayGroupDetailsViewModel();

    const fullBreadcrumbs = useMemo(() => [
        ...breadcrumbs,
        {
            index: 3,
            name: relayGroupDetails?.name ?? '',
            link: generatePath(routePaths.adminTenantRelayDetails, {
                tenantId: tenantId ?? '',
                relayGroupId: relayGroupId ?? '',
            }),
        },
    ], [ breadcrumbs, relayGroupDetails, tenantId, relayGroupId ]);
    return (
        <UiPageContainer
            loading={loading}
            position='left'
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={fullBreadcrumbs} />}>
            <RelayGroupDetailsComponent
                relayGroupDetails={relayGroupDetails}
                tenantId={tenantId}
                relayGroupId={relayGroupId}
            />
            <RelayAgentsComponent
                tenantId={tenantId}
            />
            <RelayEndpointsListComponent />
        </UiPageContainer>
    );
};

export default RelayGroupDetailsPageComponent;
